import styled, { createGlobalStyle } from 'styled-components';
import { color, container, fontSize, screen, spacing } from 'theme';

export const Fredcon26GlobalStyles = createGlobalStyle`
  /* Aggressive Root Reset */
  html, body {
    overflow: hidden !important;
    height: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Hide native cursor everywhere */
  :root, html, body, #__next, #fredcon-page-wrapper, #CybotCookiebotDialog, #freshworks-container, .react-toast-notifications__container, * {
    cursor: none !important;
  }

  /* Restore native cursor for Cookiebot dialog for usability */
  #CybotCookiebotDialog, #CybotCookiebotDialog *, #CookiebotSessionDialog, #CookiebotSessionDialog * {
    cursor: auto !important;
  }
  #CybotCookiebotDialog a, #CybotCookiebotDialog button, #CybotCookiebotDialog [role="button"], #CybotCookiebotDialog .CybotCookiebotDialogBodyButton,
  #CookiebotSessionDialog a, #CookiebotSessionDialog button, #CookiebotSessionDialog [role="button"] {
    cursor: pointer !important;
  }

  /* Restore native cursor for text inputs for usability */
  input, textarea {
    cursor: text !important;
  }

  /* Restore native cursor for disabled state if needed, though we probably want it hidden too to show our custom not-allowed state if we add it */
  [disabled], .disabled {
    cursor: none !important;
  }

  /* Ensure our custom scrollbar thumb doesn't show native cursor */
  ::-webkit-scrollbar-thumb {
    cursor: none !important;
  }
`;

export const PageWrapper = styled.div`
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: white;
  z-index: 1; /* Reset z-index since custom cursor will be 2147483647 */

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 12px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #3053f9;
    border-radius: 6px;
    border: 3px solid #f1f1f1;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #1117d7;
  }
`;

export const Root = styled.div`
  width: 100%;

  h1 {
    font-size: 128px;
    line-height: 1;
    position: relative;
    margin-bottom: 0.25rem;

    :after {
      content: '';
      display: inline-block;
      margin-left: 1.5rem;
      background: url('/images/fredcon26/logo.png') no-repeat 0 0;
      width: 115px;
      height: 115px;
      margin-bottom: -1.5rem;
    }

    @media (max-width: ${screen('lg')}) {
      font-size: 75px;
    }

    @media (max-width: ${screen('md')}) {
      font-size: 50px;
      text-align: center;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      margin-bottom: 0.75rem;

      :after {
        margin: 1rem auto 0 auto;
      }
    }
  }

  h1 span {
    display: block;
    font-weight: normal;
    font-size: ${fontSize('md')};
  }

  h2 {
    font-size: 50px;
    line-height: 1;
    position: relative;
    margin-bottom: 1.5rem;

    @media (max-width: ${screen('lg')}) {
      font-size: 42px;
    }

    @media (max-width: ${screen('md')}) {
      font-size: 28px;
    }
  }
`;

// Header -------
export const Header = styled.header`
  background-color: ${color('white')};
  width: 100%;
  overflow: hidden; /* Prevent any internal content from forcing screen width */
`;

export const HeaderContent = styled.div`
  max-width: 100%;
  margin: 0;
  padding: clamp(6px, 1vw, 12px)
    clamp(${spacing('xl')}, 1.5vw, ${spacing('xl2')});
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: clamp(56px, 7vw, 90px);
  flex-wrap: nowrap;

  @media (max-width: ${screen('md2')}) {
    justify-content: flex-start;
    gap: 8px;
    padding-left: ${spacing('md')};
    padding-right: ${spacing('md')};
    height: clamp(64px, 10vw, 130px);
  }
`;

export const HeaderGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing('md')};
  flex-wrap: nowrap;
  flex-shrink: 0; /* Don't allow the group itself to shrink below its content */
`;

export const HeaderNavLogo = styled.a`
  img {
    padding-bottom: 4px;
    padding-right: 4px;
    width: clamp(120px, calc(50vw - 400px), 200px);
    height: auto;
    display: block;

    @media (max-width: ${screen('md2')}) {
      width: 88px;
    }
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 24px;
  min-width: 0; /* Allow shrinking */

  @media (max-width: ${screen('md2')}) {
    margin-left: 0;
    flex: 1;
    justify-content: flex-end;
    gap: 16px;
  }
`;

export const HeaderNav = styled.nav`
  display: flex;
  gap: 20px;
  align-items: center;
  padding-top: 12px;

  @media (max-width: ${screen('md2')}) {
    display: none;
  }
`;

export const MobileMenuButton = styled.button`
  display: none;

  @media (max-width: ${screen('md2')}) {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #3053f9;
    color: ${color('white')};
    width: 36px;
    height: 36px;
    padding: 10px;
    border: none;
    /* cursor: pointer removed */
    z-index: 100;
  }

  @media (max-width: ${screen('smMax')}) {
    position: fixed;
    top: 12px;
    left: 16px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  }
`;

export const NavItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  /* cursor: pointer removed */
  position: relative;
  height: 32px;

  a {
    font-family: 'Archivo Narrow', sans-serif;
    font-weight: 500;
    font-size: clamp(14px, 1.2vw, 18px);
    text-transform: uppercase;
    color: #3053f9;
    line-height: 1;
    text-decoration: none;
    white-space: nowrap;
  }

  .underline {
    display: none;
    flex-wrap: wrap;
    width: 100%;
    height: 6px;
    justify-content: center;
    overflow: hidden;
    position: absolute;
    bottom: 0;
    left: 0;
  }

  &:hover {
    .underline {
      display: flex;
    }
  }
`;

export const Pixel = styled.div<{ color?: string; opacity?: number }>`
  width: 4px;
  height: 4px;
  background-color: ${({ color }) => color || '#3053f9'};
  opacity: ${({ opacity }) => (opacity !== undefined ? opacity : 1)};
`;

export const HeaderContainer = styled.div`
  max-width: ${container('xlMax')};
  margin: auto;
  padding-left: ${spacing('md')};
  padding-right: ${spacing('md')};
  display: none; /* Old implementation */
`;

export const HeaderNotice = styled.div`
  display: none; /* Not in the new design */
`;
// /Header -------

// Hero -------
export const Hero = styled.section`
  && {
    min-height: clamp(500px, 80vh, 1000px);
    width: 100%;
    position: relative;
    background: #3053f9 url('/images/fredcon26/hero-background-animated.svg')
      no-repeat center;
    background-size: cover;
    padding: ${spacing('xl5')} ${spacing('sm')};
    color: ${color('white')};
    overflow: hidden;
    display: flex;
    align-items: center;
    margin-bottom: 0;

    @media (max-width: ${screen('xl')}) {
      padding-left: ${spacing('xl')};
      padding-right: ${spacing('xl')};
    }

    @media (max-width: ${screen('md2')}) {
      min-height: 500px;
      padding: 80px ${spacing('xl')};
    }

    @media (max-width: calc(${screen('md')})) {
      background-image: url('/images/fredcon26/reticula-mobile-animated.svg');
      background-position: right center;
      padding-left: 0;
      padding-right: 0;
    }
  }
`;

export const HeroInner = styled.div`
  max-width: ${container('xl')};
  width: 100%;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 24px;
  padding: ${spacing('md')};

  @media (min-width: 939px) and (max-width: 1182px) {
    transform: translateX(calc((100vw - 939px) * 0.369));
  }

  @media (min-width: 1183px) and (max-width: 1500px) {
    transform: translateX(calc(90px - (100vw - 1183px) * 0.284));
  }
`;

export const HeroSubTitle = styled.p`
  font-family: 'Archivo Narrow', sans-serif;
  font-weight: 400;
  font-size: 20px;
  text-transform: uppercase;
  line-height: 20px;
  margin: 0;
`;

export const HeroTitle = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .logo-img {
    width: clamp(240px, 50vw, 640px);
    height: auto;
    display: block;
  }
`;

export const PoweredBy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;

  p {
    font-family: 'Archivo Narrow', sans-serif;
    font-size: 20px;
    text-transform: capitalize;
    margin: 0;
  }

  img {
    width: clamp(140px, 25vw, 280px);
    height: auto;
    display: block;
  }
`;

export const HeroTagline = styled.p`
  font-family: 'Archivo Narrow', sans-serif;
  font-size: clamp(19px, 2.2vw, 28px);
  font-weight: 400;
  line-height: 1.2;
  text-transform: uppercase;
  max-width: 700px;
  margin: 0;
`;

export const HeroDate = styled.div`
  display: inline-block;
  background-color: #ffb6fb;
  padding: 16px 20px;
  color: ${color('black')};
  max-width: 100%;

  p {
    font-family: 'Archivo Narrow', sans-serif;
    font-size: clamp(19px, 1.5vw, 20px);
    font-weight: 400;
    text-transform: uppercase;
    margin: 0;
    white-space: nowrap;

    @media (max-width: ${screen('md2')}) {
      white-space: normal;
      text-align: left;
    }
  }

  @media (max-width: ${screen('md2')}) {
    padding: 16px;
    width: 100%;
  }
`;
// /Hero -------

// What/Where -------
export const InfoWrapper = styled.section`
  && {
    width: 100%;
    position: relative;
    background: #836fff url('/images/fredcon26/reticula-2-animated.svg')
      no-repeat center;
    background-size: cover;
    background-position: center center;
    padding: ${spacing('xl5')} 0;
    color: ${color('white')};
    overflow: hidden;

    @media (max-width: ${screen('md2')}) {
      padding: 80px 0;
    }

    @media (max-width: calc(${screen('md')} - 150px)) {
      background-image: url('/images/fredcon26/reticula2-mobile-animated.svg');
      background-position: right top;
    }
  }
`;

export const InfoInner = styled.div`
  max-width: ${container('xlMax')};
  margin: auto;
  padding: 0 ${spacing('md')};
  display: flex;
  gap: 48px;
  align-items: flex-start;
  justify-content: center;

  @media (max-width: ${screen('lg')}) {
    padding: 0 ${spacing('xl')};
  }

  @media (max-width: ${screen('md2')}) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    text-align: left;
    padding: 0 ${spacing('md')};
  }
`;

export const What = styled.div`
  max-width: 540px;
  width: 100%;

  @media (max-width: ${screen('xl2')}) {
    padding-left: 120px;
  }

  @media (min-width: ${screen('md2')}) and (max-width: ${screen('lg')}) {
    padding-left: 0;
    margin: 0 auto;
  }

  @media (max-width: ${screen('md2')}) {
    padding-left: 0;
  }

  @media (max-width: 520px) {
    padding-right: 16%;
    padding-top: 12px;
  }

  h2 {
    font-family: 'Archivo Narrow', sans-serif;
    font-size: clamp(28px, 4vw, 48px);
    line-height: clamp(28px, 4vw, 48px);
    font-weight: 600;
    margin-bottom: 20px;
    color: ${color('white')};
  }
`;

export const WhatDesc = styled.div`
  font-family: 'Archivo Narrow', sans-serif;
  font-size: clamp(19px, 1.8vw, 20px);
  line-height: 1.2;
  width: 100%;

  p {
    margin: 0;
    line-height: 1.2;
  }
`;

export const Where = styled.div`
  max-width: 540px;
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: ${screen('md2')}) {
    text-align: left;
  }

  h2 {
    font-family: 'Archivo Narrow', sans-serif;
    font-size: clamp(28px, 4vw, 48px);
    line-height: clamp(28px, 4vw, 48px);
    font-weight: 600;
    margin-bottom: 20px;
    color: ${color('white')};
  }

  .where-content {
    display: flex;
    flex-direction: column;
    width: 100%;

    @media (min-width: ${screen('md')}) and (max-width: ${screen('md2')}) {
      flex-direction: row;
      gap: 24px;
      align-items: flex-start;

      > div {
        flex: 1;
      }

      iframe {
        flex: 1;
        margin-top: 0;
      }
    }
  }

  iframe {
    width: 100%;
    border-radius: 20px;
    height: 250px;
    margin-top: 24px;
    border: none;

    @media (max-width: ${screen('md2')}) {
      margin-bottom: 16px;
      height: 300px;
      margin-top: 32px;
    }
  }

  @media (max-width: ${screen('md2')}) {
    width: 100%;
  }
`;

export const WhereDesc = styled.div`
  font-family: 'Archivo Narrow', sans-serif;
  font-size: clamp(19px, 1.8vw, 20px);
  line-height: 1.2;

  p {
    margin: 0;
    line-height: 1.2;
  }
`;
// /What/Where -------

// Agenda -------
export const Agenda = styled.section`
  && {
    width: 100%;
    padding: ${spacing('xl5')} ${spacing('xl')};

    h2 {
      text-align: center;
      margin-bottom: 48px;
      font-family: 'Archivo Narrow', sans-serif;
      font-size: 52px;
      line-height: 52px;
      font-weight: 600;
      color: ${color('black')};
    }

    @media (max-width: ${screen('md2')}) {
      padding: 80px ${spacing('md')};

      h2 {
        text-align: left;
        margin-bottom: ${spacing('md')};
        font-size: 32px;
        line-height: 32px;
      }
    }
  }
`;

export const AgendaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 800px;
  margin: 0 auto;
`;

export const AgendaItem = styled.div<{ isBreak: boolean }>`
  /* This component is now replaced by Fredcon26AgendaItem.tsx */
  display: none;
`;

export const AgendaItemTime = styled.div<{ color: string }>`
  display: none;
`;

export const AgendaItemContent = styled.div`
  display: none;
`;
// /Agenda -------

// Speakers -------
export const Speakers = styled.section`
  && {
    width: 100%;
    padding: ${spacing('xl5')} ${spacing('xl3')};

    h2 {
      text-align: center;
      margin-bottom: 40px;
      font-family: 'Archivo Narrow', sans-serif;
      font-size: 52px;
      line-height: 52px;
      font-weight: 600;
      color: ${color('black')};
    }

    @media (max-width: ${screen('md2')}) {
      padding: 80px ${spacing('md')};

      h2 {
        text-align: left;
        margin-bottom: ${spacing('md')};
        font-size: 32px;
        line-height: 32px;
      }
    }
  }
`;

export const SpeakersContainer = styled.div`
  max-width: 1328px; /* 5 speakers (240px) + 4 gaps (32px) */
  margin: 0 auto;
  margin-top: ${spacing('xl2')};
  width: 100%;

  @media (max-width: 1360px) {
    max-width: 1056px; /* 4 speakers + 3 gaps */
  }

  @media (max-width: 1080px) {
    max-width: 784px; /* 3 speakers + 2 gaps */
  }

  @media (max-width: 820px) {
    max-width: 512px; /* 2 speakers + 1 gap */
  }

  @media (max-width: ${screen('md')}) {
    max-width: 100%;
  }
`;

// /Speakers -------

// Tickets -------
export const Tickets = styled.section`
  && {
    width: 100%;
    position: relative;
    background: #ffb6fb url('/images/fredcon26/reticula3-animated.svg')
      no-repeat center;
    background-size: cover;
    padding: ${spacing('xl5')} 0;
    color: ${color('black')};
    overflow: hidden;
    display: flex;
    justify-content: center;

    @media (max-width: ${screen('md2')}) {
      padding: 80px 0;
    }

    @media (max-width: calc(${screen('md')})) {
      background-image: url('/images/fredcon26/reticula3-mobile-animated.svg');
      background-position: right top;
    }
  }
`;

export const TicketsInner = styled.div`
  max-width: 540px;
  width: 100%;
  padding: 0 ${spacing('md')};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 20px;
  margin: 0 auto;

  @media (min-width: ${screen('xl2')}) {
    margin-left: calc(30vw - 57px);
    margin-right: auto;
  }

  @media (min-width: ${screen('xl')}) and (max-width: ${screen('xl2Max')}) {
    margin-left: 20vw;
    margin-right: auto;
    max-width: 46vw; /* 15% wider than before */
  }

  @media (min-width: ${screen('lg')}) and (max-width: ${screen('xlMax')}) {
    margin-left: 20vw;
    margin-right: 45%;
  }

  @media (min-width: ${screen('md2')}) and (max-width: ${screen('lgMax')}) {
    margin-left: calc(22.28vw - 57px);
    margin-right: auto;
    max-width: 40vw;
  }

  @media (min-width: ${screen('md')}) and (max-width: ${screen('md2')}) {
    padding-right: 35%;
  }

  @media (min-width: ${screen('sm')}) and (max-width: ${screen('md')}) {
    padding-right: 20%;
  }
`;

export const TicketsSubTitle = styled.p`
  font-family: 'Archivo Narrow', sans-serif;
  font-weight: 400;
  font-size: clamp(19px, 1.8vw, 20px);
  text-transform: uppercase;
  line-height: 20px;
  margin: 0;
`;

export const TicketsTitle = styled.h3`
  font-family: 'Archivo Narrow', sans-serif;
  font-size: clamp(28px, 4vw, 52px);
  line-height: 1.1;
  font-weight: 600;
  margin: 0;
  color: ${color('black')};
  max-width: 700px;
`;

export const TicketsDesc = styled.div`
  max-width: 500px;
  font-family: 'Archivo Narrow', sans-serif;
  font-size: clamp(19px, 1.8vw, 20px);
  font-weight: 400;
  line-height: 1.5;
  color: ${color('black')};
`;

export const TicketsWrapper = styled.div`
  /* Replaced by Tickets component with animated background */
  display: none;
`;
// /Tickets -------

// Partners -------
export const Partners = styled.section`
  && {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 32px;
    padding: ${spacing('xl5')} ${spacing('xl')};
    padding-bottom: ${spacing('xs')};
    margin-bottom: 0;

    h2 {
      text-align: center;
      margin: 0;
      font-family: 'Archivo Narrow', sans-serif;
      font-size: 52px;
      line-height: 52px;
      font-weight: 600;
      color: ${color('black')};
    }

    @media (max-width: ${screen('md2')}) {
      padding: 80px ${spacing('md')};
      gap: 32px;

      h2 {
        font-size: 32px;
        line-height: 32px;
      }
    }
  }
`;

export const Tiers = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
  align-items: center;

  @media (max-width: ${screen('md2')}) {
    gap: 48px;
  }
`;

export const Tier = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  align-items: center;
`;

export const PartnerList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 32px 48px;
  align-items: center;
  justify-content: center;
  width: 100%;

  &.partner-list-3-max {
    max-width: 800px;
    margin: 0 auto;
  }
`;

export const Partner = styled.a<{ tier?: 'king' | 'partner' | 'collaborator' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  flex-shrink: 0;

  img {
    height: ${({ tier }) => {
      if (tier === 'king') return '72px';
      if (tier === 'partner') return '44px';
      return '28px';
    }};
    width: auto;
    max-width: 100%;
    object-fit: contain;
    display: block;
    margin: auto;
    flex-shrink: 0;
    /* Prevent image distortion in some browsers */
    aspect-ratio: auto;
  }

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: ${screen('md2')}) {
    img {
      height: ${({ tier }) => {
        if (tier === 'king') return '60px';
        if (tier === 'partner') return '40px';
        return '32px';
      }};
    }
  }
`;

export const PartnersDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #becffe;
  margin: 12px 0 0 0;
`;

export const PartnersFooterText = styled.div`
  max-width: 942px;

  p {
    font-family: 'Archivo Narrow', sans-serif;
    font-size: 20px;
    line-height: 30px;
    color: ${color('black')};
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      font-weight: 700;
    }

    a {
      color: #3053f9;
      text-decoration: underline;
    }
  }

  @media (max-width: ${screen('md2')}) {
    p {
      font-size: 19px;
      line-height: 24px;
    }
  }
`;
// /Partners -------

// Footer -------
export const Footer = styled.footer`
  position: relative;
  width: 100%;
  background-color: white;
  margin-top: 0;
  overflow-x: hidden;

  @media (max-width: ${screen('md')}) {
    background: white
      url('/images/fredcon26/reticula-footer-mobile-animated.svg') no-repeat
      bottom center;
    background-size: cover;
    background-position: center bottom;
    padding-bottom: 2vh;
    margin-top: 0;
  }
`;

export const FooterContent = styled.div`
  width: 100%;
  margin: 2rem auto 0 auto;
  display: flex;
  flex-direction: column;
`;

export const FooterRow1 = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 80%;
  margin: 0 auto;
  position: relative;
  padding: 7% 4rem 0 4rem;
  gap: clamp(32px, 8vw, 120px);

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100vw;
    height: 100%;
    background: white url('/images/fredcon26/reticula-footer-animated.svg')
      no-repeat top center;
    background-size: 100% auto;
    background-position: right top;
    z-index: 0;
  }

  /* Ensure content is above the pseudo-element background */
  > * {
    position: relative;
    z-index: 1;
  }

  @media (min-width: ${screen('lg')}) {
    min-height: 200px;
  }

  @media (max-width: ${screen('lg')}) {
    padding-bottom: 2%;
  }

  @media (max-width: ${screen('md2')}) {
    max-width: 100%;
    padding: 5% 4rem 0 4rem;
    align-items: center;
    gap: clamp(16px, 4vw, 32px);
    min-height: 120px;
  }

  @media (max-width: ${screen('md')}) {
    flex-direction: column;
    align-items: center;
    gap: 48px;
    max-width: 100%;
    padding: ${spacing('lg')} ${spacing('md')} 0 ${spacing('md')};
    min-height: auto;

    &:before {
      display: none;
    }
  }
`;

export const FredConLogoBlack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;

  span {
    font-family: 'Archivo Narrow', sans-serif;
    font-size: clamp(14px, 1.2vw, 20px);
    line-height: 1;
    color: ${color('black')};
  }

  img {
    height: clamp(20px, 2.5vw, 42px);
    width: auto;
  }

  @media (max-width: ${screen('md')}) {
    align-items: center;
    text-align: center;

    span {
      font-size: 24px;
    }

    img {
      height: 52px;
    }
  }
`;

export const PoweredByGoogleBlack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;

  span {
    font-family: 'Archivo Narrow', sans-serif;
    font-size: clamp(14px, 1.2vw, 20px);
    line-height: 1;
    color: ${color('black')};
  }

  img {
    height: clamp(16px, 1.8vw, 32px);
    width: auto;
  }

  @media (max-width: ${screen('md')}) {
    align-items: center;
    text-align: center;

    span {
      font-size: 24px;
    }

    img {
      height: 40px;
    }
  }
`;

export const ManfredLogoMonocolor = styled.div`
  img {
    height: clamp(12px, 1.2vw, 20px);
    width: auto;
  }

  @media (max-width: ${screen('md')}) {
    img {
      height: 24px;
    }
  }
`;

export const FooterRow2 = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 942px;
  margin: 8px auto 20px auto;
  padding: 0 ${spacing('md')};
  gap: 32px;

  @media (max-width: ${screen('md2')}) {
    gap: 12px;
  }

  @media (max-width: ${screen('md')}) {
    flex-direction: column;
    gap: 24px;
    text-align: center;
    margin: 0 auto;
    padding-bottom: 0;
  }
`;

export const Copyright = styled.span`
  font-family: 'Archivo Narrow', sans-serif;
  font-size: 16px;
  color: ${color('black')};

  strong {
    font-weight: 700;
  }
`;

export const FooterLinks = styled.div`
  display: flex;
  gap: 24px;

  a {
    font-family: 'Archivo Narrow', sans-serif;
    font-size: 16px;
    text-decoration: none;
    color: #3053f9;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: ${screen('md2')}) {
    gap: 8px;
  }

  @media (max-width: ${screen('md')}) {
    flex-direction: column;
    gap: 16px;
  }
`;

export const SocialLinks = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;

  a {
    color: #3053f9;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.7;
    }

    svg,
    div {
      width: 20px;
      height: 20px;
      font-size: 20px;

      @media (max-width: ${screen('md')}) {
        width: 32px;
        height: 32px;
        font-size: 32px;
      }
    }
  }
`;
// /Footer -------
